import { AlertDTO, CreateAlert } from '@meshwatch/types';
import {
  CreateTableOutput,
  DeleteItemOutput,
  DocumentClient,
  PutItemInputAttributeMap,
  TransactWriteItemList,
} from 'aws-sdk/clients/dynamodb';
import { AWSError } from 'aws-sdk/lib/error';
import { PromiseResult } from 'aws-sdk/lib/request';
import uuid from 'uuid';

import { DEFAULT_DYNAMO_DB_CLIENT, DynamoDBClient } from '../../shared/aws/dynamodb';
import { NotFoundException } from '../../shared/rest';
import DateUtils from '../../util/date';
import DynamoMonitoringDatasource from '../db/datasource';
import { completeGettingStartedTaskUpdate } from '../../user/db/model';
import { UserDatasource, DynamoUserDatasource } from '../../user';

import { AlertDynamoItem, AlertSortKey } from './types';

export interface AlertDatasource {
  createTable: () => Promise<PromiseResult<CreateTableOutput, AWSError>>;
  createAlert: (payload: CreateAlert) => Promise<AlertDTO>;
  updateAlert: (monitor: AlertDTO) => Promise<AlertDTO>;
  getAlert: (userId: string, monitorId: string, alertId: string) => Promise<AlertDTO>;
  listAlertsForMonitor: (userId: string, monitorId: string) => Promise<AlertDTO[]>;
  listAlerts: (userId: string) => Promise<AlertDTO[]>;
  deleteAlert: (
    userId: string,
    monitorId: string,
    alertId: string
  ) => Promise<PromiseResult<DeleteItemOutput, AWSError>>;
}

export class DynamoAlertDatasource extends DynamoMonitoringDatasource implements AlertDatasource {
  private readonly userDatasource: UserDatasource;

  public constructor(dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT) {
    super(dynamoClient);
    this.userDatasource = new DynamoUserDatasource(dynamoClient);
  }

  public createAlert = async (payload: CreateAlert) => {
    const taskName = 'createAlert';
    const alert: AlertDTO = { ...payload, id: uuid.v4(), created: new Date() };
    const Item = this.alertToDynamoItem(alert) as PutItemInputAttributeMap;
    const TransactItems: TransactWriteItemList = [{ Put: { Item, TableName: this.tableName } }];

    const userInfo = await this.userDatasource.getUserInfo(alert.userId);
    if (!userInfo.gettingStarted.completedTasks[taskName]) {
      const completeTaskUpdateItemInput = completeGettingStartedTaskUpdate(
        alert.userId,
        alert.created,
        taskName
      );
      TransactItems.push({ Update: completeTaskUpdateItemInput });
    }

    return this.dynamoRawClient.transactWriteItems({ TransactItems }).then(_ => alert);
  };

  public deleteAlert = (userId: string, monitorId: string, alertId: string) => {
    const hashKey = DynamoMonitoringDatasource.encodeHashKey({ userId });
    const sortKey = this.encodeSortKey({ monitorId, alertId });

    return this.dynamoClient.deleteItem({
      Key: {
        hashKey,
        sortKey,
      },
    });
  };

  public getAlert = (userId: string, monitorId: string, alertId: string): Promise<AlertDTO> => {
    const hashKey = DynamoMonitoringDatasource.encodeHashKey({ userId });
    const sortKey = this.encodeSortKey({ monitorId, alertId });

    return this.dynamoClient
      .queryTable({
        KeyConditionExpression: `hashKey = :hashKey and sortKey = :sortKey`,
        ExpressionAttributeValues: {
          ':hashKey': hashKey,
          ':sortKey': sortKey,
        },
      })
      .then(resp => {
        if (!resp.Items || resp.Items.length === 0) {
          throw new NotFoundException(`Could not find alert id = ${alertId}`);
        }
        return this.alertFromDynamoItem(resp.Items[0] as AlertDynamoItem);
      });
  };

  public listAlerts = (userId: string): Promise<AlertDTO[]> => {
    const sortKeyStartsWith = 'alert';
    return this.searchAlerts(userId, sortKeyStartsWith);
  };

  public listAlertsForMonitor = (userId: string, monitorId: string): Promise<AlertDTO[]> => {
    const sortKeyStartsWith = `alert#${monitorId}`;
    return this.searchAlerts(userId, sortKeyStartsWith);
  };

  private searchAlerts = (userId: string, sortKeyStartsWith: string): Promise<AlertDTO[]> => {
    return this.dynamoClient
      .queryTable({
        KeyConditionExpression: `hashKey = :hashKey and begins_with(sortKey, :sortKey)`,
        ExpressionAttributeValues: {
          ':hashKey': DynamoMonitoringDatasource.encodeHashKey({ userId }),
          ':sortKey': sortKeyStartsWith,
        },
      })
      .then(this.mapDynamoRows);
  };

  public updateAlert = (payload: AlertDTO): Promise<AlertDTO> => {
    return this._putAlert(payload);
  };

  public _putAlert = (alert: AlertDTO): Promise<AlertDTO> => {
    const Item = this.alertToDynamoItem(alert);
    return this.dynamoClient.putDocumentToDynamo({ Item }).then(_ => alert);
  };

  private alertToDynamoItem = (alert: AlertDTO): AlertDynamoItem => {
    return {
      hashKey: DynamoMonitoringDatasource.encodeHashKey(alert),
      sortKey: this.encodeSortKey({ alertId: alert.id, monitorId: alert.monitorId }),
      scheduler: alert.scheduler,
      created: DateUtils.getUnixTimestamp(alert.created),
      type: alert.type,
      action: alert.action,
      actionValue: alert.actionValue,
      operation: alert.operation,
      value: alert.value,
      windowDuration: alert.windowDuration,
    };
  };

  private mapDynamoRows = (
    resp: PromiseResult<DocumentClient.QueryOutput, AWSError>
  ): AlertDTO[] => {
    const rows = resp.Items as AlertDynamoItem[];
    return rows.map(this.alertFromDynamoItem);
  };

  /* sortKey */
  private encodeSortKey({ monitorId, alertId }: AlertSortKey): string {
    return `alert#${monitorId}#${alertId}`;
  }
}

const dynamoAlertDatasource = new DynamoAlertDatasource();

export default dynamoAlertDatasource;
